/**
 * IntakeWizardPage — full-page route for reviewing & approving a single Oscar intake.
 *
 * Route: /intakes/:jiraKey/review. Reads the Jira key from the URL, fetches the intake
 * (cache-first via useIntake), and hands it to the IntakeWizard. Non-leadership users are
 * redirected to /projects (intakes are an approval surface). Includes a back link to /intakes.
 */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useIntake } from '@/hooks/useIntakes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { IntakeWizard } from '@/components/intakes/IntakeWizard';

function IntakeWizardPage(): JSX.Element {
  const navigate = useNavigate();
  const { jiraKey = '' } = useParams<{ jiraKey: string }>();
  const { user } = useAuth();
  const canApprove = user?.role === 'admin' || user?.role === 'leadership';

  const { data: intake, isLoading, isError, error, refetch, isFetching } = useIntake(jiraKey);

  // Intakes are an approval surface — non-leadership users don't belong here.
  useEffect(() => {
    if (user && !canApprove) navigate('/projects', { replace: true });
  }, [user, canApprove, navigate]);

  if (!canApprove) return <></>;

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2 text-text-secondary"
        onClick={() => navigate('/intakes')}
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Intakes
      </Button>

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Skeleton className="h-64 w-full lg:col-span-2" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      )}

      {isError && !isLoading && !intake && (
        <Alert variant="destructive">
          <AlertTitle>Failed to load intake</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {error instanceof Error ? error.message : 'This intake could not be loaded.'}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-2" onClick={() => refetch()}>
                <RefreshCw className={isFetching ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
                Retry
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/intakes')}>
                Back to Intakes
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && intake && <IntakeWizard key={intake.jira_key} intake={intake} />}

      {!isLoading && !isError && !intake && (
        <Card>
          <CardContent className="px-6 py-16 text-center text-sm text-text-secondary">
            Intake “{jiraKey}” was not found.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default IntakeWizardPage;
